import axios from 'axios';
import {
  checkSessionUrl,
  defaultHeaders,
  healthCheckLoginUrl,
  healthCheckSessionUrl,
  loginUrl,
} from '../definition/API';
import { SessionThunksI } from '../session/types';
import { log } from '../utils';

import {
  AxiosDispatchFuncType,
  AxiosDispatchType,
  DispatchedActionType,
  ErrorHandlerType,
  RequestHandlerType,
} from './types';

const unauthorizedHandlerIgnoredUrls = [
  checkSessionUrl,
  healthCheckSessionUrl,
  loginUrl,
  healthCheckLoginUrl,
];

let axiosDispatch: AxiosDispatchType;

let axiosSessionThunks: SessionThunksI;

const instance = axios.create({
  headers: defaultHeaders,
  withCredentials: true,
});

export const setDispatchForAxios: AxiosDispatchFuncType = (dispatch) => {
  axiosDispatch = dispatch;
};

export const setSessionThunksForAxios = (thunks: SessionThunksI): void => {
  axiosSessionThunks = thunks;
};

const logout: DispatchedActionType = () => {
  axiosDispatch &&
    axiosSessionThunks &&
    axiosDispatch(axiosSessionThunks.logout());
};

const verifySession: DispatchedActionType = () => {
  axiosDispatch &&
    axiosSessionThunks &&
    axiosDispatch(axiosSessionThunks.check());
};

const handleSpecialStatus = (responseStatus: number): void => {
  switch (responseStatus) {
    case 401:
      logout();
      return;
    case 403:
      verifySession();
      return;
    default:
      return;
  }
};

const getErrorLevel = (status: number) => {
  if (status >= 500) {
    return 'error';
  }
  if (status === 403) {
    return 'warning';
  }
  return 'debug';
};

const handleErrors: ErrorHandlerType = (error) => {
  const {
    request: { responseURL },
  } = error;

  if (
    error.response &&
    error.response.status &&
    !unauthorizedHandlerIgnoredUrls.some(
      (ignoredUrl) => responseURL.indexOf(ignoredUrl) !== -1
    )
  ) {
    handleSpecialStatus(error.response.status);
  }

  if (error.response && error.response.status !== 401 && responseURL) {
    const { status, statusText } = error.response;
    const logLevel = getErrorLevel(status);

    log(
      logLevel,
      `Status code: ${status}`,
      statusText,
      responseURL,
      JSON.stringify(error.response.data)
    );
  }

  return error.response || error;
};

const requestHandler: RequestHandlerType = (request) => {
  return request;
};

instance.interceptors.request.use(requestHandler, handleErrors);
instance.interceptors.response.use((response) => response, handleErrors);

export default instance;
